import { crmTaskErrorText } from 'helper/crmTaskErrors';
import useText from 'hooks/useText';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * The comments of something (a task) and what sends one. `load()` resolves
 * the list, `add(body, mentions)` the saved comment; the thread toasts who
 * was named. `people` is the team, to name the mentioned ones.
 *
 * `edit(commentId, body, mentions)` goes through `update(commentId, body,
 * mentions)`, which resolves the comment as saved; `removeComment(commentId)`
 * through `remove(commentId)`. Both resolve true once done, false on a
 * failure, and send one request per comment at a time (`pendingIds`). A
 * comment the api no longer has (COMMENT_NOT_FOUND) leaves the list.
 */
const useCommentThread = ({ key, load, add, update, remove, people, onCommented }) => {
  const { text, plural } = useText('comments');
  const { text: tasksText } = useText('tasks');
  const [comments, setComments] = useState(null);
  const [pendingIds, setPendingIds] = useState([]);
  // A ref, not the state: two clicks in the same tick would both see the
  // comment free and send twice.
  const pendingRef = useRef(new Set());
  const showError = useCallback(
    (error) => {
      const message = crmTaskErrorText(error, tasksText);
      if (message) toast.error(message);
    },
    [tasksText],
  );
  // Read through a ref so that the load effect keeps depending on `key` only.
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  // The thread reloads when it shows something else (`key`), not each time
  // the caller draws a new `load`.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    setComments(null);
    loadRef.current().then(
      (list) => alive && setComments(list),
      (error) => {
        if (!alive) return;
        showErrorRef.current(error);
        setComments([]);
      },
    );
    return () => {
      alive = false;
    };
  }, [key]);

  const send = useCallback(
    async (body, mentions) => {
      try {
        const comment = await add(body, mentions);
        setComments((list) => [...(list ?? []), comment]);
        const names = people
          .filter((person) => mentions.includes(person.id))
          .map((person) => `${person.firstname} ${person.lastname}`.trim());
        toast.success(names.length ? plural('sentWithMentions', names.length, { names: names.join(', ') }) : text('added'));
        onCommented?.();
        return true;
      } catch (error) {
        showError(error);
        return false;
      }
    },
    [add, people, onCommented, text, plural, showError],
  );

  const dropComment = useCallback(
    (commentId) => setComments((list) => (list ?? []).filter((comment) => comment.id !== commentId)),
    [],
  );

  // Runs `request` for one comment, unless one is already under way for it.
  const runOnComment = useCallback(
    async (commentId, request, onDone) => {
      if (pendingRef.current.has(commentId)) return false;
      pendingRef.current.add(commentId);
      setPendingIds((ids) => [...ids, commentId]);
      try {
        onDone(await request());
        onCommented?.();
        return true;
      } catch (error) {
        if (error?.response?.data?.errorCode === 'COMMENT_NOT_FOUND') dropComment(commentId);
        showError(error);
        return false;
      } finally {
        pendingRef.current.delete(commentId);
        setPendingIds((ids) => ids.filter((id) => id !== commentId));
      }
    },
    [onCommented, dropComment, showError],
  );

  const edit = useCallback(
    (commentId, body, mentions) =>
      runOnComment(
        commentId,
        () => update(commentId, body, mentions),
        (saved) => {
          setComments((list) => (list ?? []).map((comment) => (comment.id === commentId ? saved : comment)));
          toast.success(text('updated'));
        },
      ),
    [runOnComment, update, text],
  );

  const removeComment = useCallback(
    (commentId) =>
      runOnComment(
        commentId,
        () => remove(commentId),
        () => {
          dropComment(commentId);
          toast.success(text('deleted'));
        },
      ),
    [runOnComment, remove, dropComment, text],
  );

  return { comments, send, edit, removeComment, pendingIds };
};

export default useCommentThread;
